import { shipExtent } from "./ship";
import type { Ship, World } from "./types";

export const SHIP_GRID_CELL_M = 64;
const KEY_OFFSET = 1 << 15;

function cellKey(x: number, y: number): number {
  return (((x + KEY_OFFSET) & 0xffff) << 16) | ((y + KEY_OFFSET) & 0xffff);
}

export interface ShipGrid {
  cells: Map<number, Ship[]>;
  cellSize: number;
}

const _cellPool: Ship[][] = [];

export function buildShipGrid(world: World): ShipGrid {
  const grid: ShipGrid = { cells: new Map(), cellSize: SHIP_GRID_CELL_M };
  for (const ship of world.ships.values()) {
    insert(grid, ship);
  }
  return grid;
}

export function releaseShipGrid(grid: ShipGrid): void {
  for (const arr of grid.cells.values()) {
    arr.length = 0;
    _cellPool.push(arr);
  }
  grid.cells.clear();
}

function insert(grid: ShipGrid, ship: Ship): void {
  const ext = shipExtent(ship);
  const minX = ship.body.pos.x - ext;
  const maxX = ship.body.pos.x + ext;
  const minY = ship.body.pos.y - ext;
  const maxY = ship.body.pos.y + ext;
  const cs = grid.cellSize;
  const x0 = Math.floor(minX / cs);
  const x1 = Math.floor(maxX / cs);
  const y0 = Math.floor(minY / cs);
  const y1 = Math.floor(maxY / cs);
  for (let cx = x0; cx <= x1; cx++) {
    for (let cy = y0; cy <= y1; cy++) {
      const k = cellKey(cx, cy);
      let arr = grid.cells.get(k);
      if (!arr) {
        arr = _cellPool.pop() ?? [];
        grid.cells.set(k, arr);
      }
      arr.push(ship);
    }
  }
}

/** Multi-cell ships re-visit; caller dedupes. Return false to stop. */
export function forEachShipAlongRay(
  grid: ShipGrid,
  ox: number, oy: number,
  dx: number, dy: number,
  maxT: number,
  visit: (ship: Ship, cellEntryT: number) => boolean | void,
): void {
  const cs = grid.cellSize;
  let cx = Math.floor(ox / cs);
  let cy = Math.floor(oy / cs);

  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;

  let tMaxX: number;
  let tDeltaX: number;
  if (stepX !== 0) {
    const nextCellX = (stepX > 0 ? cx + 1 : cx) * cs;
    tMaxX = (nextCellX - ox) / dx;
    tDeltaX = cs / Math.abs(dx);
  } else {
    tMaxX = Infinity;
    tDeltaX = Infinity;
  }

  let tMaxY: number;
  let tDeltaY: number;
  if (stepY !== 0) {
    const nextCellY = (stepY > 0 ? cy + 1 : cy) * cs;
    tMaxY = (nextCellY - oy) / dy;
    tDeltaY = cs / Math.abs(dy);
  } else {
    tMaxY = Infinity;
    tDeltaY = Infinity;
  }

  let t = 0;
  while (t <= maxT) {
    const arr = grid.cells.get(cellKey(cx, cy));
    if (arr) {
      for (let i = 0; i < arr.length; i++) {
        if (visit(arr[i], t) === false) return;
      }
    }
    if (tMaxX < tMaxY) {
      t = tMaxX;
      tMaxX += tDeltaX;
      cx += stepX;
    } else {
      t = tMaxY;
      tMaxY += tDeltaY;
      cy += stepY;
    }
    if (tMaxX === Infinity && tMaxY === Infinity) break;
  }
}

export function forEachCellAlongLocalRay(
  ox: number, oy: number,
  dx: number, dy: number,
  cellSize: number,
  maxT: number,
  visit: (gx: number, gy: number, cellEntryT: number) => boolean | void,
): void {
  let cx = Math.floor(ox / cellSize);
  let cy = Math.floor(oy / cellSize);

  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;

  let tMaxX: number;
  let tDeltaX: number;
  if (stepX !== 0) {
    const nextCellX = (stepX > 0 ? cx + 1 : cx) * cellSize;
    tMaxX = (nextCellX - ox) / dx;
    tDeltaX = cellSize / Math.abs(dx);
  } else {
    tMaxX = Infinity;
    tDeltaX = Infinity;
  }

  let tMaxY: number;
  let tDeltaY: number;
  if (stepY !== 0) {
    const nextCellY = (stepY > 0 ? cy + 1 : cy) * cellSize;
    tMaxY = (nextCellY - oy) / dy;
    tDeltaY = cellSize / Math.abs(dy);
  } else {
    tMaxY = Infinity;
    tDeltaY = Infinity;
  }

  let t = 0;
  while (t <= maxT) {
    if (visit(cx, cy, t) === false) return;
    if (tMaxX < tMaxY) {
      t = tMaxX;
      tMaxX += tDeltaX;
      cx += stepX;
    } else {
      t = tMaxY;
      tMaxY += tDeltaY;
      cy += stepY;
    }
    if (tMaxX === Infinity && tMaxY === Infinity) break;
  }
}
